import { ISceneAdapter } from './ISceneAdapter';
import { IPhysicsAdapter } from './IPhysicsAdapter';

/**
 * Адаптер для Phaser.Scene, реализующий интерфейс ISceneAdapter
 * Позволяет использовать Phaser-сцену через абстрактный интерфейс
 */
export class PhaserSceneAdapter implements ISceneAdapter {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Создает спрайт на сцене
   * @param x - координата X
   * @param y - координата Y
   * @param texture - текстура спрайта
   */
  createSprite(x: number, y: number, texture: string): any {
    // Возвращаем спрайт с физикой, чтобы обеспечить совместимость с IProjectile
    return this.scene.physics.add.sprite(x, y, texture);
  }

  /**
   * Добавляет объект на сцену
   * @param object - объект для добавления
   */
  add(object: any): void {
    // В зависимости от типа объекта добавляем его соответствующим образом
    if (object instanceof Phaser.GameObjects.Sprite) {
      this.scene.add.existing(object);
    } else if (object instanceof Phaser.GameObjects.Graphics) {
      this.scene.add.existing(object);
    } else if (object instanceof Phaser.GameObjects.Text) {
      this.scene.add.existing(object);
    } else {
      // Если объект не является стандартным GameObject, пытаемся добавить его как есть
      this.scene.add.existing(object);
    }
  }

  /**
   * Запускает таймер
   * @param delay - задержка в миллисекундах
   * @param callback - функция обратного вызова
   */
  timeDelayCall(delay: number, callback: () => void): void {
    this.scene.time.delayedCall(delay, callback);
  }

  /**
   * Добавляет объект в физическое тело
   * @param object - объект для добавления
   */
  addPhysics(object: any): any {
    return this.scene.physics.add.existing(object);
  }

  /**
   * Получает размеры сцены
   */
  getSceneBounds(): { width: number; height: number } {
    return {
      width: this.scene.scale.width,
      height: this.scene.scale.height
    };
  }

  /**
   * Добавляет слушатель события
   * @param event - тип события
   * @param callback - функция обратного вызова
   */
  on(event: string, callback: (...args: any[]) => void): void {
    this.scene.events.on(event, callback);
  }

  /**
   * Удаляет слушатель события
   * @param event - тип события
   * @param callback - функция обратного вызова
   */
  off(event: string, callback: (...args: any[]) => void): void {
    this.scene.events.off(event, callback);
  }

  /**
   * Получает позицию прокрутки камеры
   */
  getCameraScroll(): { x: number; y: number } {
    return {
      x: this.scene.cameras.main.scrollX,
      y: this.scene.cameras.main.scrollY
    };
  }
}

/**
 * Адаптер для Phaser.Physics, реализующий интерфейс IPhysicsAdapter
 */
export class PhaserPhysicsAdapter implements IPhysicsAdapter {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Создает физическое тело для объекта
   * @param object - объект для создания физического тела
   * @param x - координата X
   * @param y - координата Y
   * @param texture - текстура спрайта
   */
  createPhysicsBody(object: any, x: number, y: number, texture: string): any {
    // Если объект еще не является спрайтом с физикой, создаем его
    if (!object.body) {
      return this.scene.physics.add.sprite(x, y, texture);
    }
    return object;
  }

  /**
   * Устанавливает скорость объекта
   * @param body - физическое тело
   * @param x - скорость по X
   * @param y - скорость по Y
   */
  setVelocity(body: any, x: number, y: number): void {
    if (body && body.setVelocity) {
      body.setVelocity(x, y);
    }
  }

  /**
   * Устанавливает активность физического тела
   * @param body - физическое тело
   * @param value - значение активности
   */
  setActive(body: any, value: boolean): void {
    if (body && body.setActive) {
      body.setActive(value);
    }
  }

  /**
   * Устанавливает видимость физического тела
   * @param body - физическое тело
   * @param value - значение видимости
   */
  setVisible(body: any, value: boolean): void {
    if (body && body.setVisible) {
      body.setVisible(value);
    }
  }

  /**
   * Устанавливает гравитацию для тела
   * @param body - физическое тело
   * @param x - гравитация по X
   * @param y - гравитация по Y
   */
  setGravity(body: any, x: number, y: number): void {
    if (body && body.setGravity) {
      body.setGravity(x, y);
    }
  }

  /**
   * Проверяет коллизию между двумя объектами
   * @param object1 - первый объект
   * @param object2 - второй объект
   * @param callback - функция обратного вызова при коллизии
   */
  checkCollision(object1: any, object2: any, callback: () => void): void {
    this.scene.physics.add.overlap(object1, object2, callback);
  }

  /**
   * Устанавливает массу тела
   * @param body - физическое тело
   * @param mass - масса
   */
  setMass(body: any, mass: number): void {
    if (body && body.setMass) {
      body.setMass(mass);
    }
  }

  /**
   * Устанавливает импульс телу
   * @param body - физическое тело
   * @param x - импульс по X
   * @param y - импульс по Y
   */
  setImpulse(body: any, x: number, y: number): void {
    if (body && body.body && body.body.setVelocity) {
      // Phaser использует setVelocity для импульса
      body.body.setVelocity(x, y);
    }
  }
}